// contexts/JWTContext.jsx
import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';

// third-party
import Chance from 'chance';
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

import Loader from 'components/Loader';
import { fleetApi } from '../api/axiosClient';
import axios from 'axios';
import { API_BASES } from '../config/constants';
const chance = new Chance();

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// safe verify token
const verifyToken = (serviceToken) => {
  if (!serviceToken) return false;
  try {
    const decoded = jwtDecode(serviceToken);
    if (!decoded || typeof decoded.exp !== 'number') return false;
    return decoded.exp > Date.now() / 1000;
  } catch (e) {
    console.warn('verifyToken error', e);
    return false;
  }
};

const setSession = (serviceToken) => {
  if (serviceToken) {
    try {
      localStorage.setItem('serviceToken', serviceToken);
    } catch (e) {
      console.warn('localStorage set serviceToken failed', e);
    }
    fleetApi.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    try {
      localStorage.removeItem('serviceToken');
    } catch (e) {
      console.log(e);
    }
    delete fleetApi.defaults.headers.common.Authorization;
  }
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');

        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          let userFromToken = null;
          try {
            const decoded = jwtDecode(serviceToken);
            if (decoded) {
              userFromToken = decoded.user ?? decoded.sub ?? null;
            }
          } catch (e) {
            console.log(e);
            userFromToken = null;
          }

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              isInitialized: true,
              user: userFromToken
            }
          });
        } else {
          dispatch({ type: LOGOUT, payload: { isInitialized: true } });
        }
      } catch (err) {
        console.error('JWTProvider init error', err);
        dispatch({ type: LOGOUT, payload: { isInitialized: true } });
      }
    };

    init();
  }, []);

  const login = async (username, password, remember = true) => {
    const resp = await fleetApi.post('user/login', { username, password });
    const wrapper = resp?.data ?? {};
    const payload = wrapper?.data ?? wrapper;

    const serviceToken = payload?.apikey ?? payload?.apikey_jyoti ?? payload?.token ?? payload?.serviceToken ?? null;
    let user = null;
    if (payload) {
      user = {
        id: payload.id,
        fullname: payload.fullname,
        company: payload.company,
        role_name: payload.role_name,
        username: payload.username
      };
    }

    if (!serviceToken) {
      throw new Error('Login gagal: token tidak diterima dari server');
    }

    setSession(serviceToken);
    // let fetchedPackageName = [];

    let roleDataList = [];

    try {
      const roleSearchPayload = {
        paging: { start: 0, length: 1000 },
        columns: [
          { name: 'role_parent', logic_operator: '=', value: payload.role_name, operator: 'OR' },
          { name: 'role_name', logic_operator: '=', value: payload.role_name, operator: 'OR' }
        ]
      };
      const url = `${API_BASES.FLEET_API_GO}/master-role/search`;

      const roleResp = await axios.post(url, roleSearchPayload, {
        headers: {
          Authorization: `Bearer ${serviceToken}`
        }
      });
      const roleData = roleResp?.data?.data || roleResp?.data || [];

      if (roleData.length > 0) {
        roleDataList = roleData;
      }
    } catch (err) {
      console.warn('Gagal fetch data master-role untuk mendapatkan package_name', err);
    }
    const matchedRole = roleDataList.find((role) => role.role_name === payload.role_name) || 'Undifine';

    const store = {
      dataRole: JSON.stringify(roleDataList),
      user: JSON.stringify(user),
      user_id: String(payload?.id ?? payload?.user_id ?? ''),
      apiKey: payload?.apikey ?? payload?.apiKey ?? '',
      apikey_jyoti: payload?.apikey_jyoti ?? '',
      companyId: String(payload?.company ?? payload?.company_id ?? ''),
      name: payload?.fullname ?? payload?.name ?? '',
      rolename: payload?.role_name ?? payload?.rolename ?? '',
      package_name: matchedRole.package_name,
      package: matchedRole,
      appSetting: JSON.stringify(payload?.appSetting ?? payload?.app_setting ?? {})
    };

    try {
      Object.entries(store).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        localStorage.setItem(k, typeof v === 'string' ? v : JSON.stringify(v));
      });
    } catch (e) {
      console.warn('Gagal menyimpan ke localStorage', e);
    }

    if (!remember) {
      try {
        Object.keys(store).forEach((k) => {
          const v = localStorage.getItem(k);
          if (v) sessionStorage.setItem(k, v);
        });
      } catch (e) {
        console.warn('Gagal menyalin ke sessionStorage', e);
      }
    }

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        isInitialized: true,
        user
      }
    });

    return { user, serviceToken };
  };

  const register = async (email, password, firstName, lastName) => {
    const id = chance.bb_pin();
    const response = await fleetApi.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;
    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }
    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = async ({ revokeEndpoint = null } = {}) => {
    try {
      const serviceToken = (() => {
        try {
          return window.localStorage.getItem('serviceToken');
        } catch {
          return null;
        }
      })();

      if (revokeEndpoint && serviceToken) {
        try {
          await fleetApi.post(revokeEndpoint, { token: serviceToken }).catch(() => {});
        } catch (e) {
          console.warn('Token revoke failed', e);
        }
      }
      setSession(null);
      const keysToRemove = [
        'user',
        'user_id',
        'apiKey',
        'apikey_jyoti',
        'companyId',
        'name',
        'master_menu',
        'token',
        'rolename',
        'package',
        'package_name',
        'appSetting',
        'serviceToken',
        'menu_translations',
        'howen_token',
        'selectedVehicleIds',
        'dataRole',
        'active_ritase_data',
        'fuel_fromDate',
        'fuel_periodType',
        'fuel_toDate',
        'vehicle_routes',
        'vehicle_installed_camera'
      ];

      try {
        keysToRemove.forEach((k) => {
          try {
            localStorage.removeItem(k);
          } catch (e) {
            console.log(e);
          }
        });

        // Clear dynamic dashboard caches
        Object.keys(localStorage).forEach((k) => {
          if (
            k.startsWith('revenue_') ||
            k.startsWith('asset_') ||
            k.startsWith('report_trip_') ||
            k.startsWith('periodeRange_') ||
            k.startsWith('cache_')
          ) {
            localStorage.removeItem(k);
          }
        });
      } catch (e) {
        console.warn('Error clearing localStorage keys', e);
      }
      try {
        keysToRemove.forEach((k) => {
          try {
            sessionStorage.removeItem(k);
          } catch (e) {
            console.log(e);
          }
        });
      } catch (e) {
        console.warn('Error clearing sessionStorage keys', e);
      }
      try {
        if (typeof document !== 'undefined' && document.cookie) {
          document.cookie.split(';').forEach((c) => {
            const eqPos = c.indexOf('=');
            const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
            if (!name) return;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          });
        }
      } catch (e) {
        console.warn('Clearing cookies failed', e);
      }
      if (typeof window !== 'undefined' && 'caches' in window) {
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        } catch (e) {
          console.warn('Clearing caches failed', e);
        }
      }
      dispatch({ type: LOGOUT, payload: { isInitialized: true } });

      return true;
    } catch (err) {
      console.error('logout error', err);
      try {
        setSession(null);
      } catch {}
      try {
        dispatch({ type: LOGOUT, payload: { isInitialized: true } });
      } catch {}
      return false;
    }
  };

  useEffect(() => {
    const interceptor = fleetApi.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response && error.response.status === 401) {
          console.warn('Terdeteksi 401 Unauthorized. Menjalankan auto-logout...');
          await logout();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      fleetApi.interceptors.response.eject(interceptor);
    };
  }, []);

  const resetPassword = async (email) => {
    console.log('resetPassword called for', email);
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        updateProfile
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;
